// src/services/membershipMails.js
// Wrapper de fetch autenticado
import { fetchWithAuth } from '../utils/fetchWithAuth';

/* -------------------------------------------
   Constantes reutilizables
-------------------------------------------- */
export const DEFAULT_DECLARATION_FILE_NAME =
  'Formato Declaración Ingreso Cidere Región de Coquimbo.docx';

/* -------------------------------------------
   Helpers para armar la URL pública del front
-------------------------------------------- */

/**
 * Devuelve la URL absoluta de un archivo en el /public del FRONT.
 * Maneja espacios/acentos con encodeURIComponent.
 * Ej: getFrontendPublicUrl('Formato Declaración Ingreso Cidere Región de Coquimbo.docx')
 */
export function getFrontendPublicUrl(fileName) {
  const origin =
    (typeof window !== 'undefined' && window.location && window.location.origin) || '';
  if (!origin) throw new Error('No se pudo resolver window.location.origin');
  const clean = String(fileName || '').replace(/^\/+/, ''); // sin slash inicial
  return `${origin}/${encodeURIComponent(clean)}`;
}

/* -------------------------------------------
   Helpers internos
-------------------------------------------- */
const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

function toEmailArray(v) {
  if (!v) return undefined;
  const arr = Array.isArray(v) ? v : String(v).split(/[,\s;]+/);
  const norm = arr
    .map((s) => String(s).trim())
    .filter((s) => emailRx.test(s));
  return norm.length ? norm : undefined;
}

/* -------------------------------------------
   Normalizador de respuestas (robusto)
-------------------------------------------- */
async function normalize(res) {
  // Si es un Response de fetch
  if (res && typeof res === 'object' && typeof res.ok === 'boolean') {
    const status = res.status;
    let data = null;
    try {
      data = await res.json();
    } catch (_) {}
    return { ok: status >= 200 && status < 300, status, data };
  }
  // Fallback si ya es objeto "data"
  const status = typeof res?.status === 'number' ? res.status : 200;
  const ok = status >= 200 && status < 300 && res?.ok !== false;
  return { ok, status, data: res };
}

/* -------------------------------------------
   Endpoints de mails de membresía (frontend)
-------------------------------------------- */

/**
 * (Opcional) Preview de plantilla por tipo
 * GET /membership-mails/:id/preview?template=ACCEPTED
 */
export async function previewMembershipMail(appId, template) {
  const res = await fetchWithAuth(
    `/membership-mails/${appId}/preview?template=${encodeURIComponent(template)}`,
    { method: 'GET' }
  );
  const out = await normalize(res);
  if (!out.ok) {
    const msg =
      out.data?.message ||
      out.data?.error ||
      `No se pudo cargar el preview de correo (HTTP ${out.status}).`;
    throw new Error(msg);
  }
  return out.data;
}

/**
 * Envía el correo de una postulación con adjuntos opcionales.
 *
 * appId:               number
 * options = {
 *   template:          'RECEIVED'|'ACCEPTED'|'REJECTED'|'MISSING',
 *   to:                string,           // destinatario
 *   subject?:          string,
 *   body?:             string,           // texto plano; el backend genera html con firma
 *   copyToMe?:         boolean,          // agrega CCs por defecto desde el backend
 *   cc?:               string[]|string,  // CC extra (string con comas o array)
 *   bcc?:              string[]|string,  // BCC extra
 *   date?:             string,           // ISO YYYY-MM-DD (opcional)
 *   attachDeclaration?:boolean,          // adjuntar Declaración Jurada
 *   declarationPath?:  string,           // URL absoluta o nombre relativo (el backend soporta ambos)
 *   attachApplicationPdf?: boolean,      // adjunta ficha PDF si el backend detecta viewUrl
 *   attachments?:      Array<{ filename?:string, path?:string, url?:string, contentType?:string }>,
 *   attachUrls?:       string[]          // URLs directas a adjuntar
 * }
 *
 * POST /membership-mails/:id/send
 */
export async function sendMembershipMail(appId, options = {}) {
  const {
    template,
    to,
    subject,
    body,
    copyToMe = true,
    cc,
    bcc,
    date,
    attachDeclaration = false,
    declarationPath,
    attachApplicationPdf = false,
    attachments = [],
    attachUrls = [],
  } = options;

  // Normaliza CC/BCC: acepta string o array
  const ccNorm = toEmailArray(cc);
  const bccNorm = toEmailArray(bcc);

  // Fallback automático para la declaración si la pidieron y no pasaron ruta
  let declarationPathFinal = declarationPath;
  if (attachDeclaration && !declarationPathFinal) {
    try {
      declarationPathFinal = getFrontendPublicUrl(DEFAULT_DECLARATION_FILE_NAME);
    } catch {
      // si no hay window (SSR) o falla, lo dejamos undefined,
      // el backend igualmente puede resolver por nombre si lo envías luego.
    }
  }

  const payload = {
    template,
    to,
    subject,
    body,
    copyToMe,
    ...(ccNorm ? { cc: ccNorm } : {}),
    ...(bccNorm ? { bcc: bccNorm } : {}),
    date,
    attachDeclaration,
    ...(declarationPathFinal ? { declarationPath: declarationPathFinal } : {}),
    attachApplicationPdf,
    attachments,
    attachUrls,
  };

  const res = await fetchWithAuth(`/membership-mails/${appId}/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const out = await normalize(res);
  if (!out.ok) {
    const msg =
      out.data?.message ||
      out.data?.error ||
      (Array.isArray(out.data?.errors) ? out.data.errors.join('\n') : null) ||
      `No se pudo enviar el correo (HTTP ${out.status}).`;
    throw new Error(msg);
  }
  return out.data;
}

/* -------------------------------------------
   Gestión de plantillas de correo
-------------------------------------------- */

/**
 * Obtiene todas las plantillas guardadas
 * GET /membership-mails/templates
 */
export async function getMailTemplates() {
  const res = await fetchWithAuth('/membership-mails/templates', {
    method: 'GET',
  });
  const out = await normalize(res);
  if (!out.ok) {
    const msg =
      out.data?.message ||
      out.data?.error ||
      `No se pudieron cargar las plantillas (HTTP ${out.status}).`;
    throw new Error(msg);
  }
  return out.data;
}

/**
 * Obtiene una plantilla específica
 * GET /membership-mails/templates/:kind
 */
export async function getMailTemplate(kind) {
  const res = await fetchWithAuth(`/membership-mails/templates/${encodeURIComponent(kind)}`, {
    method: 'GET',
  });
  const out = await normalize(res);
  if (!out.ok) {
    const msg =
      out.data?.message ||
      out.data?.error ||
      `No se pudo cargar la plantilla (HTTP ${out.status}).`;
    throw new Error(msg);
  }
  return out.data;
}

/**
 * Guarda o actualiza una plantilla
 * PUT /membership-mails/templates/:kind
 */
export async function saveMailTemplate(kind, subject, body) {
  const res = await fetchWithAuth(`/membership-mails/templates/${encodeURIComponent(kind)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ subject, body }),
  });
  const out = await normalize(res);
  if (!out.ok) {
    const msg =
      out.data?.message ||
      out.data?.error ||
      `No se pudo guardar la plantilla (HTTP ${out.status}).`;
    throw new Error(msg);
  }
  return out.data;
}

/**
 * Elimina una plantilla (restaura a valores por defecto)
 * DELETE /membership-mails/templates/:kind
 */
export async function deleteMailTemplate(kind) {
  const res = await fetchWithAuth(`/membership-mails/templates/${encodeURIComponent(kind)}`, {
    method: 'DELETE',
  });
  const out = await normalize(res);
  if (!out.ok) {
    const msg =
      out.data?.message ||
      out.data?.error ||
      `No se pudo restaurar la plantilla (HTTP ${out.status}).`;
    throw new Error(msg);
  }
  return out.data;
}

/**
 * Obtiene los correos CC por defecto desde MEMBERSHIP_CC_DEFAULT
 * GET /membership-mails/cc-default
 */
export async function getCcDefault() {
  const res = await fetchWithAuth('/membership-mails/cc-default', {
    method: 'GET',
  });
  const out = await normalize(res);
  if (!out.ok) {
    const msg =
      out.data?.message ||
      out.data?.error ||
      `No se pudo obtener el CC por defecto (HTTP ${out.status}).`;
    throw new Error(msg);
  }
  return out.data || [];
}